import { FC, useMemo, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ColumnDef } from "@tanstack/react-table";
import { AddIcon, ArrowRightIcon, MinusIcon } from "@chakra-ui/icons";
import {
  Flex,
  Skeleton,
  Stack,
  Tag,
  TagLabel,
  TagLeftIcon,
  VStack,
  Heading,
  Text,
  Center,
  Spinner,
} from "@chakra-ui/react";

import { Table, Panel, PageHeading, TableRowActions } from "@bemap/ui";
import { ITeam, ICredit, CreditType, formatDate, handleAssessmentStatus, handleAssessmentTag, handleReportType, calculateTotalCredits, getConsumptionType } from "@bemap/utils";
import { getAnalystById, getCreditHistory } from "../../services/analysts-service";
import { useTranslation } from "react-i18next";

interface IAssessment {
  id: number;
  status: string;
  report_id: number;
  created_at: string;
}

interface InfoRowProps {
  isLoading: boolean;
  title: string;
  text?: string;
}

const InfoRow: FC<InfoRowProps> = ({ isLoading, title, text }) => {
  return (
    <VStack align="start" spacing={1}>
      <Text fontSize="sm" fontWeight="bold" color="gray.600">
        {title}
      </Text>
      {isLoading ? (
        <Skeleton height="20px" width="150px" />
      ) : (
        <Text>{text || '--'}</Text>
      )}
    </VStack>
  );
};

const ViewAnalystPage: FC = () => {
  const { analystId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const initialAnalyst: ITeam = {
    id: 0,
    name: '',
    userId: 0,
    personalTeam: false,
    type: "",
    profilePhotoPath: "",
    user: {
      id: 0,
      name: '',
      email: '',
      phone: '',
      password: '',
      totalCredits: 0,
    },
    assessments: []
  };

  const [analyst, setAnalyst] = useState<ITeam>(initialAnalyst);
  const [credits, setCredits] = useState<ICredit[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [updateData, setUpdateData] = useState<string>("");

  async function loadAnalyst() {
    setIsLoading(true);
    try {
      const response = await getAnalystById(parseInt(analystId || '0'));
      setAnalyst(response.message);
      setUpdateData(new Date().toDateString());
      setIsLoading(false);
    } catch (error) {
      setAnalyst(initialAnalyst);
      setIsLoading(false);
    }
  }

  async function loadCreditHistory() {
    setIsLoadingHistory(true);
    try {
      const response = await getCreditHistory(parseInt(analystId || '0'));
      setCredits(response.message);
      setIsLoadingHistory(false);
    } catch (error) {
      setCredits([]);
      setIsLoadingHistory(false);
    }
  }

  useEffect(() => {
    loadAnalyst();
  }, []);

  useEffect(() => {
    if (analyst?.id) {
      loadCreditHistory();
    }
  }, [updateData, analyst?.id]);

  function handleCreditType(type: CreditType | undefined) {
    switch (type) {
      case CreditType.ADD:
        return 'Crédito Adicionado';
      case CreditType.TRANSFER:
        return 'Crédito Adicionado';
      case CreditType.USE:
        return 'Crédito Consumido';
      default:
        return 'Transação';
    }
  }

  function handleCreditIconTag(type: string) {
    switch (type) {
      case 'Crédito Adicionado':
        return (
          <Tag size={'sm'} variant='subtle' colorScheme='green'>
            <TagLeftIcon boxSize='12px' as={AddIcon} />
            <TagLabel>{type}</TagLabel>
          </Tag>
        );
      case 'Crédito Retirado':
        return (
          <Tag size={'sm'} variant='subtle' colorScheme='red'>
            <TagLeftIcon boxSize='12px' as={MinusIcon} />
            <TagLabel>{type}</TagLabel>
          </Tag>
        );
      case 'Crédito Consumido':
        return (
          <Tag size={'sm'} variant='subtle' colorScheme='blue'>
            <TagLeftIcon boxSize='12px' as={ArrowRightIcon} />
            <TagLabel>{type}</TagLabel>
          </Tag>
        );
      default:
        return (
          <Tag size={'sm'} variant='subtle' colorScheme='gray'>
            <TagLabel>{type}</TagLabel>
          </Tag>
        );
    }
  }

  const columns = useMemo<ColumnDef<ICredit, any>[]>(
    () => [
      {
        accessorFn: (row) => handleCreditType(row.type),
        id: "Transação",
        header: t('admin.credits') || 'Transação',
        cell: (info) => handleCreditIconTag(info.getValue()),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => t(getConsumptionType(row) ?? ''),
        id: "Destino",
        header: t('admin.credit_table_destiny') || 'Destino',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.amount,
        id: "Quantidade",
        header: t('admin.credit_table_quantity') || 'QTD',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row.created_at,
        id: "Data da ação",
        header: t('analyst.assesments_table_created') || 'Data',
        cell: (info) => formatDate(info.getValue(), true),
        footer: (props) => props.column.id,
      },
    ],
    []
  );

  const assessmentColumns = useMemo<ColumnDef<IAssessment, any>[]>(
    () => [
      {
        accessorFn: (row) => t(handleReportType(row.report_id ?? 0) ?? ''),
        id: "Tipo de Relatório",
        header: t('analyst.assesments_new_type') || 'Tipo',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => formatDate(row.created_at, true),
        header: t('analyst.assesments_table_created') || 'Data',
        cell: (info) => info.getValue(),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => handleAssessmentStatus(row.status ?? ''),
        id: "Status",
        header: "STATUS",
        cell: (info) => (
          <Tag size='sm' colorScheme={handleAssessmentTag(info.getValue() ?? '') ?? 'blue'}>
            {t(info.getValue() ?? '')}
          </Tag>
        ),
        footer: (props) => props.column.id,
      },
      {
        accessorFn: (row) => row,
        id: "Ações",
        enableColumnFilter: false,
        enableHiding: true,
        header: t('table.actions') || 'Actions',
        cell: (row) => {
          const assessment = row.getValue();
          return (
            <TableRowActions
              viewAction={() => navigate(`/assessments/${assessment.id}/results`)}
            />
          );
        },
      },
    ],
    []
  );

  if (isLoading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <>
      <VStack gap="20px" align={'stretch'} w="100%">
        <PageHeading title={analyst?.name || 'Analista'} />
        <Panel padding={4}>
          <Flex gap={8} wrap="wrap">
            <InfoRow isLoading={isLoading} title={t('analyst.assesments_table_email')} text={analyst?.user?.email} />
            <InfoRow isLoading={isLoading} title={t('analyst.assesments_table_email')} text={analyst?.user?.phone} />
            <InfoRow isLoading={isLoading} title={t('partner.certificates_status_created')} text={formatDate(analyst?.createdAt ?? '')} />
            <InfoRow isLoading={isLoading} title={t('admin.credits')} text={calculateTotalCredits(credits).toString()} />
          </Flex>
        </Panel>

        <Heading size="md" mt={4} alignSelf="start">Relatórios</Heading>
        <Panel>
          {isLoading ? (
            <Stack mx="20px" my="20px">
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
              <Skeleton height="20px" isLoaded={!isLoading} />
            </Stack>
          ) : (
            analyst?.assessments && analyst.assessments.length > 0 ? (
              <Table
                tableId="analyst-assessments-table"
                columns={assessmentColumns}
                data={analyst.assessments}
              />
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                Nenhum relatório encontrado
              </Text>
            )
          )}
        </Panel>

        <Heading size="md" mt={4} alignSelf="start">Histórico de Créditos</Heading>
        <Panel>
          {isLoadingHistory ? (
            <Stack mx="20px" my="20px">
              <Skeleton height="20px" isLoaded={!isLoadingHistory} />
              <Skeleton height="20px" isLoaded={!isLoadingHistory} />
              <Skeleton height="20px" isLoaded={!isLoadingHistory} />
            </Stack>
          ) : (
            credits && credits.length > 0 ? (
              <Table
                tableId="analyst-credits-table"
                columns={columns}
                data={credits}
              />
            ) : (
              <Text color="gray.500" textAlign="center" py={8}>
                Nenhum histórico de créditos encontrado
              </Text>
            )
          )}
        </Panel>
      </VStack>
    </>
  );
};

export default ViewAnalystPage; 